import React from 'react';
import { Link } from 'react-router-dom';
import {
  Target, Sparkles, Users, Mail, MessageSquare, ArrowRight,
  Zap, Radar, PenLine, Map,
} from 'lucide-react';

/**
 * Marketing showcase for the "Precise Application Path" premium feature.
 * A visually rich section for the landing page that sells the novelty:
 * AI that builds a bespoke, multi-tiered plan to land a specific job.
 */

const STEPS = [
  { icon: Radar, label: 'Match', desc: 'We rank the roles you’re most likely to land.', color: 'from-sky-500 to-cyan-400' },
  { icon: Target, label: 'Position', desc: 'AI crafts your angle from your real skills.', color: 'from-indigo-500 to-violet-500' },
  { icon: Users, label: 'Reach', desc: 'Find the right people & likely work emails.', color: 'from-fuchsia-500 to-pink-500' },
  { icon: Map, label: 'Outreach', desc: 'A staged plan: engage → connect → message.', color: 'from-amber-500 to-orange-500' },
];

const PreciseApplicationPathShowcase: React.FC = () => {
  return (
    <section className="relative overflow-hidden bg-primary-600 py-24">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-indigo-600/30 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-fuchsia-600/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.08),transparent_60%)]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-indigo-400/30 bg-indigo-500/10 px-4 py-1.5 text-sm font-medium text-white">
            <Sparkles className="h-4 w-4" />
            New · Powered by Fable 5
          </span>
          <h2 className="mt-6 text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Don’t just apply.{' '}
            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-amber-300 bg-clip-text text-transparent">
              Get a precise path in.
            </span>
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-300">
            Everyone else fires off the same résumé into the void. <strong className="text-white">Precise Application
            Path</strong> builds you a personalised game plan for a specific job — who to contact, what to say, and a
            step-by-step outreach sequence that actually gets replies.
          </p>
        </div>

        {/* Pipeline */}
        <div className="relative mt-16">
          <div className="absolute left-0 right-0 top-9 hidden h-px bg-gradient-to-r from-transparent via-white/15 to-transparent lg:block" />
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {STEPS.map((step, i) => (
              <div key={step.label} className="group relative">
                <div className="flex flex-col items-center text-center">
                  <div
                    className={`relative flex h-18 w-18 items-center justify-center rounded-2xl bg-gradient-to-br ${step.color} p-0.5 shadow-lg transition-transform duration-300 group-hover:-translate-y-1`}
                    style={{ height: '4.5rem', width: '4.5rem' }}
                  >
                    <div className="flex h-full w-full items-center justify-center rounded-2xl bg-slate-950/80">
                      <step.icon className="h-8 w-8 text-white" />
                    </div>
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-bold text-slate-900">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-white">{step.label}</h3>
                  <p className="mt-1 text-sm text-white">{step.desc}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <ArrowRight className="absolute -right-3 top-8 hidden h-6 w-6 text-white/30 lg:block" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Split: value props + app preview */}
        <div className="mt-20 grid items-center gap-12 lg:grid-cols-2">
          {/* Value props */}
          <div className="space-y-6">
            <ValueProp
              icon={<Users className="h-5 w-5" />}
              title="Finds the right people"
              text="Surfaces the exact roles worth contacting at the company — with best-guess work-email patterns so you can actually reach them."
            />
            <ValueProp
              icon={<PenLine className="h-5 w-5" />}
              title="Writes your outreach"
              text="Ready-to-send, personalised email drafts tailored to the hiring manager and the team — grounded in your real skills."
            />
            <ValueProp
              icon={<MessageSquare className="h-5 w-5" />}
              title="A multi-tier strategy"
              text="A staged sequence — engage with their posts, connect, then a thoughtful direct message — so you build a genuine relationship, not spam."
            />
            <div className="pt-2">
              <Link
                to="/precise-application-path"
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-6 py-3 font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:shadow-indigo-500/40"
              >
                <Zap className="h-5 w-5" />
                Try Precise Application Path
                <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-3 text-xs text-slate-500">Included with Student Plus &amp; Pro.</p>
            </div>
          </div>

          {/* App preview card */}
          <div className="relative">
            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-indigo-500/40 to-fuchsia-500/40 blur-lg" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-slate-900/80 backdrop-blur">
              <div className="flex items-center justify-between border-b border-white/10 bg-white/5 px-5 py-3">
                <div className="flex items-center gap-2 text-white">
                  <Target className="h-4 w-4 text-indigo-400" />
                  <span className="text-sm font-semibold">Your path · Frontend Engineer @ Nimbus</span>
                </div>
                <span className="rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-xs font-semibold text-emerald-300">
                  92% match
                </span>
              </div>
              <div className="space-y-4 p-5">
                <MockBlock label="Positioning angle">
                  Lead with your real-time dashboard project — it maps directly to their data-viz roadmap.
                </MockBlock>

                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-fuchsia-300">
                    <Users className="h-3.5 w-3.5" /> Who to reach
                  </div>
                  <div className="text-sm text-slate-200">Maya Chen · Eng. Manager, Web</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {['m.chen@nimbus.io', 'mchen@nimbus.io'].map((e) => (
                      <code key={e} className="rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-300">{e}</code>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-300">
                    <Mail className="h-3.5 w-3.5" /> Draft
                  </div>
                  <div className="text-sm text-slate-300">
                    <span className="text-slate-400">Subject:</span> Loved Nimbus’ new charts — a quick idea
                  </div>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-400">
                    Hi Maya — I built a live analytics dashboard handling 10k events/sec and noticed…
                  </p>
                </div>

                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-300">
                    <Map className="h-3.5 w-3.5" /> Outreach plan
                  </div>
                  <ol className="space-y-1.5 text-sm text-slate-300">
                    {[
                      'Day 1 · Comment on Maya’s post about their design system',
                      'Day 3 · Connect on LinkedIn with a one-line note',
                      'Day 5 · Send the tailored email above',
                    ].map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-[10px] font-bold text-white">
                          {i + 1}
                        </span>
                        {s}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ValueProp: React.FC<{ icon: React.ReactNode; title: string; text: string }> = ({ icon, title, text }) => (
  <div className="flex gap-4">
    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500/20 to-fuchsia-500/20 text-indigo-300 ring-1 ring-white/10">
      {icon}
    </div>
    <div>
      <h3 className="font-semibold text-white">{title}</h3>
      <p className="mt-1 text-sm leading-6 text-slate-300">{text}</p>
    </div>
  </div>
);

const MockBlock: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <p className="text-sm text-slate-200">{children}</p>
  </div>
);

export default PreciseApplicationPathShowcase;
